import { components } from "@/api-schema";
import { RelocationOrderStatus } from "@/api-schema-enums";
import { isRelocationOrderTerminalStatus } from "@/common/helpers/relocation-order-terminal-status";
import { useRelocationOrderInternalActions } from "@/common/hooks/relocation-order/use-relocation-order-internal-actions";
import { useUrlEnumState } from "@/common/hooks/use-url-enum-state";
import { createRowNavigatePath } from "@/components/data-table/_helpers/create-row-navigate-path";
import { RelocationOrderTable } from "@/components/data-table/predefined/relocation-order-table";
import { relocationTableOrderDefaultColumns } from "@/components/data-table/predefined/relocation-order-table-columns";
import { DataTableHeightMode } from "@/components/data-table/types";
import { DeviceAware } from "@/components/device-aware/device-aware";
import type { Filter } from "@/components/filter/_types";
import { BranchFilter } from "@/components/filter/predefined/branch-filter";
import { OrderedByMeFilter } from "@/components/filter/predefined/ordered-by-me-filter";
import {
  RELOCATION_ORDER_STATUS_FILTER_OPTIONS,
  RelocationOrderStatusFilter,
} from "@/components/filter/predefined/relocation-order-status-filter";
import { RelocationOrderTransportMethodFilter } from "@/components/filter/predefined/relocation-order-transport-method-filter";
import { RelocationOrderTransportReasonFilter } from "@/components/filter/predefined/relocation-order-transport-reason-filter";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { MultiToggle } from "@/components/multi-toggle/multi-toggle";
import { CommandButton } from "@/components/command-palette/command-button";
import { buttonVariants, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { userStore } from "@/store/user-store";
import { PlaneLandingIcon, PlaneTakeoffIcon, PlusCircleIcon, TruckIcon, WrenchIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";

type RelocationOrderSearchBody = components["schemas"]["RelocationOrderSearchBody"];

const INTERNAL_RELOCATION_ORDER_COLUMNS = [
  relocationTableOrderDefaultColumns.mobile.columnMobileCompleteWithTransportMethodNoColor,
  relocationTableOrderDefaultColumns.tablet.columnCompactVehicleInfoNoColor,
  relocationTableOrderDefaultColumns.desktop.columnVehicleInfoWithBrandNoColor,
  relocationTableOrderDefaultColumns.widescreen.columnBrand,
  relocationTableOrderDefaultColumns.widescreen.columnModelGenerationName,
  relocationTableOrderDefaultColumns.widescreen.columnVin,
  relocationTableOrderDefaultColumns.desktop.columnTransportMethod,
  relocationTableOrderDefaultColumns.widescreen.columnTransportMethod,
  relocationTableOrderDefaultColumns.tablet.columnTransportMethod,
  relocationTableOrderDefaultColumns.tablet.columnInternalCompactRoute,
  relocationTableOrderDefaultColumns.widescreenAndDesktop.columnInternalRouteWithStatus,
  relocationTableOrderDefaultColumns.widescreenAndDesktop.columnEarliestPickupDate,
];

const INTERNAL_RELOCATION_ORDER_STATUS_FILTER_OPTIONS = Object.values(RELOCATION_ORDER_STATUS_FILTER_OPTIONS);

enum BranchFilterMode {
  Incoming = "Incoming",
  Outgoing = "Outgoing",
  All = "All",
}

type BranchFilterId = "originOrDestinationBranch" | "destinationBranch" | "originBranch";

const BRANCH_FILTER_IDS: readonly BranchFilterId[] = ["originOrDestinationBranch", "destinationBranch", "originBranch"];

class RelocationOrderBranchFilter extends BranchFilter {
  readonly id: BranchFilterId = "originOrDestinationBranch";

  constructor(id: BranchFilterId, ...args: ConstructorParameters<typeof BranchFilter>) {
    super(...args);
    this.id = id;
  }
}

function getBranchFilterId(branchFilterMode: BranchFilterMode): BranchFilterId {
  switch (branchFilterMode) {
    case BranchFilterMode.Incoming:
      return "destinationBranch";
    case BranchFilterMode.Outgoing:
      return "originBranch";
    case BranchFilterMode.All:
    default:
      return "originOrDestinationBranch";
  }
}

const TODO_STATUSES: RelocationOrderStatus[] = [
  RelocationOrderStatus.TransportCompanyAwaitingReadyForTransport,
  RelocationOrderStatus.TransportCompanyRejectedDamagedVehicle,
  RelocationOrderStatus.TransportCompanyRejectedMissingKeys,
  RelocationOrderStatus.TransportCompanyRejectedMissingVehicle,
  RelocationOrderStatus.TransportCompanyAwaitingDeliveryAcceptance,
  RelocationOrderStatus.BranchRejectedDamagedVehicle,
  RelocationOrderStatus.BranchRejectedMissingKeys,
  RelocationOrderStatus.BranchRejectedMissingVehicle,
  RelocationOrderStatus.BranchRejectedWrongVehicle,
];

export function RelocationOrderView() {
  const { t } = useTranslation(["fit", "common"]);
  const pageTitle = t("fit:RelocationOrders");
  const pageSubtitle = t("fit:OverviewOfAllRelocationOrders");
  const { user } = userStore();
  const { userBranchesUuids, isLoadingUserBranches, canManage, customRowActions, dialogs } =
    useRelocationOrderInternalActions();

  const defaultStatuses = useMemo(() => {
    return INTERNAL_RELOCATION_ORDER_STATUS_FILTER_OPTIONS.filter(
      (status) => !isRelocationOrderTerminalStatus(status.id),
    ).map((status) => status.id);
  }, []);

  const [selectedBranches, setSelectedBranches] = useState<string[]>([]);
  const [filterValues, setFilterValues] = useState({
    orderedBy: [],
    status: defaultStatuses,
    transportMethod: [],
    transportReason: [],
  });

  const [branchFilterMode, setBranchFilterMode] = useUrlEnumState<BranchFilterMode>(
    "direction",
    Object.values(BranchFilterMode),
    BranchFilterMode.All,
  );
  const activeBranchFilterId = useMemo(() => getBranchFilterId(branchFilterMode), [branchFilterMode]);

  const isTodoFilterActive = useMemo(() => {
    const currentStatuses = new Set(filterValues.status);
    const todoStatuses = new Set(TODO_STATUSES);

    return currentStatuses.size === todoStatuses.size && TODO_STATUSES.every((status) => currentStatuses.has(status));
  }, [filterValues.status]);

  const handleFilterChange = useCallback((filterId: string, values: string[]) => {
    if (BRANCH_FILTER_IDS.includes(filterId as BranchFilterId)) {
      setSelectedBranches(values);
      return;
    }

    setFilterValues((prev) => ({ ...prev, [filterId]: values }));
  }, []);

  const tableFilterValues = useMemo(
    () => ({
      ...(selectedBranches.length > 0 ? { [activeBranchFilterId]: selectedBranches } : {}),
      ...filterValues,
    }),
    [activeBranchFilterId, filterValues, selectedBranches],
  );

  const relocationOrderBaseBody = useMemo(() => {
    const baseBody: RelocationOrderSearchBody = {};

    if (!canManage) {
      if (branchFilterMode === BranchFilterMode.All) {
        baseBody.originOrDestinationBranch = userBranchesUuids;
        baseBody.includeCurrentUsersOrders = true;
      } else if (branchFilterMode === BranchFilterMode.Incoming) {
        baseBody.destinationBranch = userBranchesUuids;
      } else {
        baseBody.originBranch = userBranchesUuids;
      }
    }

    if (filterValues.transportMethod.length > 0) {
      baseBody.transportMethod = filterValues.transportMethod;
    }

    if (filterValues.transportReason.length > 0) {
      baseBody.transportReason = filterValues.transportReason;
    }

    return Object.keys(baseBody).length > 0 ? baseBody : undefined;
  }, [canManage, userBranchesUuids, branchFilterMode, filterValues.transportMethod, filterValues.transportReason]);

  const filters = useMemo(() => {
    const nextFilters: Filter<unknown, unknown>[] = [];

    if (user.uuid) {
      nextFilters.push(new OrderedByMeFilter(user.uuid));
    }

    nextFilters.push(
      new RelocationOrderBranchFilter(
        activeBranchFilterId,
        canManage ? undefined : { body: { uuid: userBranchesUuids } },
      ),
      new RelocationOrderStatusFilter(INTERNAL_RELOCATION_ORDER_STATUS_FILTER_OPTIONS),
      new RelocationOrderTransportMethodFilter(),
      new RelocationOrderTransportReasonFilter(),
    );

    return nextFilters;
  }, [activeBranchFilterId, canManage, user.uuid, userBranchesUuids]);

  const createButtons = useMemo(
    () => (
      <div className="flex flex-wrap justify-end gap-2">
        <Link className={buttonVariants()} to="~/fit/relocation-order/init/select-vehicle">
          <PlusCircleIcon />
          {t("fit:CreateRelocationOrder")}
        </Link>
        {canManage && (
          <Link
            className={buttonVariants({
              variant: ButtonVariant.Outlined,
              colorVariant: ButtonColorVariant.Secondary,
            })}
            to="~/fit/relocation-order/create/select-vehicle"
          >
            <WrenchIcon />
            {t("fit:AdminCreate")}
          </Link>
        )}
      </div>
    ),
    [canManage, t],
  );

  const toggleTodoFilter = useCallback(
    (active: boolean) => {
      const statusesToFilter = active ? TODO_STATUSES : (defaultStatuses as RelocationOrderStatus[]);
      handleFilterChange("status", statusesToFilter);
    },
    [handleFilterChange, defaultStatuses],
  );

  if (isLoadingUserBranches) {
    return (
      <CenteredLayout variant={CenteredLayoutVariant.Table} title={pageTitle} subTitle={pageSubtitle}>
        <div className="space-y-6">
          <Skeleton className="h-14 w-lg" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-200 w-full" />
        </div>
      </CenteredLayout>
    );
  }

  return (
    <CenteredLayout variant={CenteredLayoutVariant.Table} title={pageTitle} subTitle={pageSubtitle}>
      <div className="flex min-h-0 flex-1 flex-col gap-0">
        <DeviceAware
          mobile={
            <div className="relative flex w-full flex-row justify-center">
              <MultiToggle
                aria-label={t("fit:RelocationOrderDirectionFilterLabel")}
                className="w-full max-w-100"
                options={[
                  { label: t("fit:Outgoing"), icon: PlaneTakeoffIcon, value: BranchFilterMode.Outgoing },
                  { label: t("fit:All"), value: BranchFilterMode.All },
                  { label: t("fit:Incoming"), icon: PlaneLandingIcon, value: BranchFilterMode.Incoming },
                ]}
                onChange={(value: BranchFilterMode) => {
                  setBranchFilterMode(value);
                }}
                value={branchFilterMode}
              />
            </div>
          }
          fallback={
            <div className="relative flex flex-row items-end justify-center gap-3">
              <MultiToggle
                aria-label={t("fit:RelocationOrderDirectionFilterLabel")}
                options={[
                  { label: t("fit:Outgoing"), icon: PlaneTakeoffIcon, value: BranchFilterMode.Outgoing },
                  { label: t("fit:All"), value: BranchFilterMode.All },
                  { label: t("fit:Incoming"), icon: PlaneLandingIcon, value: BranchFilterMode.Incoming },
                ]}
                onChange={(value: BranchFilterMode) => {
                  setBranchFilterMode(value);
                }}
                value={branchFilterMode}
              />
            </div>
          }
        />

        <RelocationOrderTable
          className="flex-1"
          columns={INTERNAL_RELOCATION_ORDER_COLUMNS}
          body={relocationOrderBaseBody}
          filters={filters}
          filterValues={tableFilterValues}
          onFilterChange={handleFilterChange}
          filterContainerCustomContent={
            <CommandButton
              commandId="fit-relocation-order-my-todo"
              label={t("fit:MyToDo")}
              className="w-full"
              variant={isTodoFilterActive ? ButtonVariant.Filled : ButtonVariant.Outlined}
              colorVariant={isTodoFilterActive ? ButtonColorVariant.Primary : ButtonColorVariant.Secondary}
              onClick={() => toggleTodoFilter(!isTodoFilterActive)}
            />
          }
          customRowActions={customRowActions}
          onRowNavigate={createRowNavigatePath("/fit/relocation-order")}
          heightMode={DataTableHeightMode.Content}
          headerComponent={createButtons}
          noDataIcon={TruckIcon}
          noDataTitle={t("fit:NoRelocationOrdersFound")}
          noDataDescription={t("fit:NoRelocationOrdersFoundDescription")}
          noResultsTitle={t("fit:NoRelocationOrdersMatchFilters")}
          noResultsDescription={t("fit:NoRelocationOrdersMatchFiltersDescription")}
        />
      </div>
      {dialogs}
    </CenteredLayout>
  );
}
